"use client"

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { BudgetScope } from "@/features/budgeting/model/budget-enums"
import {
  addBudgetLine,
  addBudgetNote,
  approveBudget,
  archiveBudget,
  closeBudget,
  createBudget,
  deleteBudgetLine,
  deleteBudgetNote,
  listBudgetNotes,
  requestChangesBudget,
  reviewBudget,
  resubmitBudget,
  returnToDraftBudget,
  markReadyBudget,
  reviseBudget,
  updateBudgetNote,
  submitBudget,
  fetchActiveBudgetOverview,
  fetchBudgetDetail,
  fetchBudgetHistory,
  fetchBudgetLineTransactions,
  fetchBudgetTransactions,
  fetchBudgetList,
  fetchBudgetOverview,
  fetchBudgetVersions,
  patchBudget,
  patchBudgetLine,
  restoreBudget,
  type ActiveBudgetOverviewQuery,
  type BudgetPerformanceQuery,
  fetchBudgetPerformance,
  type BudgetLineTransactionsQuery,
  type BudgetListQuery,
  type BudgetOverviewQuery,
  type CreateBudgetInput,
  type CreateBudgetLineInput,
  type PatchBudgetInput,
} from "@/features/budgeting/services/budgets-api"
import { budgetQueryKeys } from "@/features/budgeting/services/budgets-query-keys"
import { WORK_QUEUE_ROOT_KEY } from "@/features/work-queue/services/work-queue-query-keys"

export function useBudgetListQuery(query: BudgetListQuery, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: budgetQueryKeys.list(query),
    queryFn: () => fetchBudgetList(query),
    enabled: options.enabled ?? true,
  })
}

export function useBudgetDetailQuery(
  id: string | undefined,
  workspaceId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  const enabled = Boolean(id) && Boolean(workspaceId) && (options.enabled ?? true)

  return useQuery({
    queryKey: budgetQueryKeys.detail(workspaceId ?? "", id ?? ""),
    queryFn: () => fetchBudgetDetail(id ?? "", workspaceId ?? ""),
    enabled,
  })
}

export function useBudgetHistoryQuery(
  id: string | undefined,
  workspaceId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  const enabled = Boolean(id) && Boolean(workspaceId) && (options.enabled ?? true)
  return useQuery({
    queryKey: budgetQueryKeys.history(workspaceId ?? "", id ?? ""),
    queryFn: () => fetchBudgetHistory(id ?? "", workspaceId ?? ""),
    enabled,
  })
}

export function useBudgetVersionsQuery(
  id: string | undefined,
  workspaceId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  const enabled = Boolean(id) && Boolean(workspaceId) && (options.enabled ?? true)
  return useQuery({
    queryKey: budgetQueryKeys.versions(workspaceId ?? "", id ?? ""),
    queryFn: () => fetchBudgetVersions(id ?? "", workspaceId ?? ""),
    enabled,
  })
}

export function useBudgetOverviewQuery(
  id: string | undefined,
  query: { workspaceId?: string; dateFrom?: string; dateTo?: string; granularity?: BudgetOverviewQuery["granularity"] },
  options: { enabled?: boolean } = {},
) {
  const requestQuery: BudgetOverviewQuery = {
    workspaceId: query.workspaceId ?? "",
    dateFrom: query.dateFrom,
    dateTo: query.dateTo,
    granularity: query.granularity,
  }
  const enabled = Boolean(id) && Boolean(query.workspaceId) && (options.enabled ?? true)

  return useQuery({
    queryKey: budgetQueryKeys.overview(id ?? "", requestQuery),
    queryFn: () => fetchBudgetOverview(id ?? "", requestQuery),
    enabled,
  })
}

export function useActiveBudgetOverviewQuery(
  query: { workspaceId?: string; context?: ActiveBudgetOverviewQuery["context"]; branchId?: string; dateFrom?: string; dateTo?: string; granularity?: ActiveBudgetOverviewQuery["granularity"] },
  // The aggregate states its own currency (IBA-114/116) — the caller no longer supplies one.
  options: { enabled?: boolean } = {},
) {
  const requestQuery: ActiveBudgetOverviewQuery = {
    workspaceId: query.workspaceId ?? "",
    context: query.context ?? BudgetScope.Branch,
    branchId: query.branchId ?? "",
    dateFrom: query.dateFrom,
    dateTo: query.dateTo,
    granularity: query.granularity,
  }
  const enabled = Boolean(query.workspaceId) && Boolean(query.context) && Boolean(query.branchId) && (options.enabled ?? true)

  return useQuery({
    queryKey: budgetQueryKeys.activeOverview(requestQuery),
    queryFn: () => fetchActiveBudgetOverview(requestQuery),
    enabled,
  })
}

/**
 * IBA-157 — planned vs realised by income account, expense account and fund, for the overview widgets.
 *
 * Every figure is computed server-side; consumers render them and derive nothing (no client-side
 * percentages, no rollups), so a widget can never disagree with the budget page about the same number.
 */
export function useBudgetPerformanceQuery(
  query: { workspaceId?: string; context?: BudgetPerformanceQuery["context"]; branchId?: string; dateFrom?: string; dateTo?: string },
  options: { enabled?: boolean } = {},
) {
  const requestQuery: BudgetPerformanceQuery = {
    workspaceId: query.workspaceId ?? "",
    context: query.context ?? BudgetScope.Branch,
    branchId: query.branchId ?? "",
    dateFrom: query.dateFrom,
    dateTo: query.dateTo,
  }
  const enabled = Boolean(query.workspaceId) && Boolean(query.branchId) && (options.enabled ?? true)

  return useQuery({
    queryKey: budgetQueryKeys.performance(requestQuery),
    queryFn: () => fetchBudgetPerformance(requestQuery),
    enabled,
  })
}

export function useBudgetLineTransactionsQuery(
  budgetId: string | undefined,
  lineId: string | undefined,
  query: { workspaceId?: string; dateFrom?: string; dateTo?: string; page?: number; limit?: number },
  options: { enabled?: boolean } = {},
) {
  const requestQuery: BudgetLineTransactionsQuery = {
    workspaceId: query.workspaceId ?? "",
    dateFrom: query.dateFrom,
    dateTo: query.dateTo,
    page: query.page,
    limit: query.limit,
  }
  const enabled = Boolean(budgetId) && Boolean(lineId) && Boolean(query.workspaceId) && (options.enabled ?? true)

  return useQuery({
    queryKey: budgetQueryKeys.lineTransactions(budgetId ?? "", lineId ?? "", requestQuery),
    queryFn: () => fetchBudgetLineTransactions(budgetId ?? "", lineId ?? "", requestQuery),
    enabled,
  })
}

export function useBudgetTransactionsQuery(
  budgetId: string | undefined,
  query: { workspaceId?: string; lineId?: string; dateFrom?: string; dateTo?: string; page?: number; limit?: number },
  options: { enabled?: boolean } = {},
) {
  const requestQuery: BudgetLineTransactionsQuery = {
    workspaceId: query.workspaceId ?? "",
    lineId: query.lineId,
    dateFrom: query.dateFrom,
    dateTo: query.dateTo,
    page: query.page,
    limit: query.limit,
  }
  const enabled = Boolean(budgetId) && Boolean(query.workspaceId) && (options.enabled ?? true)

  return useQuery({
    queryKey: budgetQueryKeys.transactions(budgetId ?? "", requestQuery),
    queryFn: () => fetchBudgetTransactions(budgetId ?? "", requestQuery),
    enabled,
  })
}

/**
 * IBA-70: unlinked transactions that match an approved/active budget's lines. Returns 409
 * BUDGET_NOT_ACTIVE for non-active budgets — the caller gates the query on the active status, and the
 * dialog surfaces any error gracefully. Scoped key (workspace + budget), never a broad key.
 */
function invalidateBudgetScope(queryClient: ReturnType<typeof useQueryClient>, workspaceId: string, budgetId?: string) {
  void queryClient.invalidateQueries({ queryKey: budgetQueryKeys.lists() })
  void queryClient.invalidateQueries({ queryKey: budgetQueryKeys.overviews(workspaceId) })
  if (budgetId) {
    void queryClient.invalidateQueries({ queryKey: budgetQueryKeys.detail(workspaceId, budgetId) })
  }
  // Budget review status drives the Work Queue (badge + Budgets tab): refresh after any budget change
  // that may move a budget into/out of PENDING_REVIEW (submit/approve/return/activate/close/archive).
  void queryClient.invalidateQueries({ queryKey: WORK_QUEUE_ROOT_KEY })
}

export function useCreateBudgetMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateBudgetInput) => createBudget(payload),
    onSuccess: (_data, payload) => {
      invalidateBudgetScope(qc, payload.workspaceId)
    },
  })
}

export function usePatchBudgetMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: PatchBudgetInput }) => patchBudget(id, payload),
    onSuccess: (_data, vars) => {
      invalidateBudgetScope(qc, vars.payload.workspaceId, vars.id)
    },
  })
}

export function useAddBudgetLineMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ budgetId, payload }: { budgetId: string; payload: { workspaceId: string; line: CreateBudgetLineInput } }) =>
      addBudgetLine(budgetId, payload),
    onSuccess: (_d, v) => {
      invalidateBudgetScope(qc, v.payload.workspaceId, v.budgetId)
    },
  })
}

export function usePatchBudgetLineMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      budgetId,
      lineId,
      payload,
    }: {
      budgetId: string
      lineId: string
      payload: { workspaceId: string } & Partial<CreateBudgetLineInput>
    }) => patchBudgetLine(budgetId, lineId, payload),
    onSuccess: (_d, v) => {
      invalidateBudgetScope(qc, v.payload.workspaceId, v.budgetId)
    },
  })
}

export function useDeleteBudgetLineMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ budgetId, lineId, workspaceId }: { budgetId: string; lineId: string; workspaceId: string }) =>
      deleteBudgetLine(budgetId, lineId, workspaceId),
    onSuccess: (_d, v) => {
      invalidateBudgetScope(qc, v.workspaceId, v.budgetId)
    },
  })
}

export function useSubmitBudgetMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, workspaceId }: { id: string; workspaceId: string }) => submitBudget(id, workspaceId),
    onSuccess: (_d, vars) => {
      invalidateBudgetScope(qc, vars.workspaceId, vars.id)
    },
  })
}

export function useApproveBudgetMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, workspaceId, comment }: { id: string; workspaceId: string; comment?: string }) =>
      approveBudget(id, { workspaceId, comment }),
    onSuccess: (_d, vars) => {
      invalidateBudgetScope(qc, vars.workspaceId, vars.id)
    },
  })
}

export function useRequestChangesBudgetMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, workspaceId, comment }: { id: string; workspaceId: string; comment?: string }) =>
      requestChangesBudget(id, { workspaceId, comment }),
    onSuccess: (_d, vars) => {
      invalidateBudgetScope(qc, vars.workspaceId, vars.id)
    },
  })
}

export function useReviewBudgetMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, workspaceId }: { id: string; workspaceId: string }) => reviewBudget(id, workspaceId),
    onSuccess: (_d, vars) => invalidateBudgetScope(qc, vars.workspaceId, vars.id),
  })
}

export function useResubmitBudgetMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, workspaceId }: { id: string; workspaceId: string }) => resubmitBudget(id, workspaceId),
    onSuccess: (_d, vars) => invalidateBudgetScope(qc, vars.workspaceId, vars.id),
  })
}

export function useReturnToDraftBudgetMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, workspaceId }: { id: string; workspaceId: string }) => returnToDraftBudget(id, workspaceId),
    onSuccess: (_d, vars) => invalidateBudgetScope(qc, vars.workspaceId, vars.id),
  })
}

export function useMarkReadyBudgetMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, workspaceId }: { id: string; workspaceId: string }) => markReadyBudget(id, workspaceId),
    onSuccess: (_d, vars) => invalidateBudgetScope(qc, vars.workspaceId, vars.id),
  })
}

export function useReviseBudgetMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, workspaceId, comment }: { id: string; workspaceId: string; comment?: string }) =>
      reviseBudget(id, { workspaceId, comment }),
    onSuccess: (_d, vars) => invalidateBudgetScope(qc, vars.workspaceId, vars.id),
  })
}

export function useCloseBudgetMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, workspaceId }: { id: string; workspaceId: string }) => closeBudget(id, workspaceId),
    onSuccess: (_d, vars) => {
      invalidateBudgetScope(qc, vars.workspaceId, vars.id)
    },
  })
}

export function useArchiveBudgetMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, workspaceId }: { id: string; workspaceId: string }) => archiveBudget(id, workspaceId),
    onSuccess: (_d, vars) => {
      invalidateBudgetScope(qc, vars.workspaceId, vars.id)
    },
  })
}

export function useRestoreBudgetMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, workspaceId }: { id: string; workspaceId: string }) => restoreBudget(id, workspaceId),
    onSuccess: (_d, vars) => {
      invalidateBudgetScope(qc, vars.workspaceId, vars.id)
    },
  })
}

/** Budget notes are loaded 10 at a time via the "Load more" button. */
const BUDGET_NOTES_PAGE_SIZE = 10

export function useBudgetNotesInfiniteQuery(
  id: string | undefined,
  workspaceId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  const enabled = Boolean(id) && Boolean(workspaceId) && (options.enabled ?? true)
  return useInfiniteQuery({
    queryKey: budgetQueryKeys.notes(workspaceId ?? "", id ?? ""),
    queryFn: ({ pageParam }) =>
      listBudgetNotes(id ?? "", { workspaceId: workspaceId ?? "", cursor: pageParam ?? undefined, limit: BUDGET_NOTES_PAGE_SIZE }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
  })
}

export function useAddBudgetNoteMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, workspaceId, body }: { id: string; workspaceId: string; body: string }) =>
      addBudgetNote(id, { workspaceId, body }),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: budgetQueryKeys.notes(vars.workspaceId, vars.id) })
    },
  })
}

export function useUpdateBudgetNoteMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, noteId, workspaceId, body }: { id: string; noteId: string; workspaceId: string; body: string }) =>
      updateBudgetNote(id, noteId, { workspaceId, body }),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: budgetQueryKeys.notes(vars.workspaceId, vars.id) })
    },
  })
}

export function useDeleteBudgetNoteMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, noteId, workspaceId }: { id: string; noteId: string; workspaceId: string }) =>
      deleteBudgetNote(id, noteId, workspaceId),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: budgetQueryKeys.notes(vars.workspaceId, vars.id) })
    },
  })
}
